import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'semantic-ui-react';
import PositionedModal from 'common/components/PositionedModal';
import { GuidedExperienceDefinition } from '../guidedExperienceRegistry';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/common/ui/select';
import { getLeadershipTeamsForSelectedAssessmentPeriod } from '@/common/selectors/auth';
import userContextSlice from '@/common/slices/userContextSlice';
import { useWelcomeTourData } from '../welcome-tour/useWelcomeTourData';

interface GuidedExperienceModalProps {
  experience: GuidedExperienceDefinition;
  stepIndex: number;
  open: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onComplete: () => void;
  onClose: () => void;
  isSaving: boolean;
}

const GuidedExperienceModal = ({
  experience,
  stepIndex,
  open,
  onPrevious,
  onNext,
  onComplete,
  onClose,
  isSaving,
}: GuidedExperienceModalProps) => {
  const dispatch = useDispatch();
  const leadershipTeams = useSelector(getLeadershipTeamsForSelectedAssessmentPeriod);
  const selectedTeamId = useSelector(userContextSlice.selectors.getSelectedTeamId as any) as number | undefined;

  const step = experience.steps[stepIndex];
  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === experience.steps.length - 1;
  const { isLoading: tourLoading } = useWelcomeTourData();
  const StepComponent = step.component;
  const [isFullscreen, setIsFullscreen] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 1100px), (max-height: 820px)')
      : null;

    const updateFullscreen = (event: MediaQueryList | MediaQueryListEvent | null) => {
      if (!event || typeof event.matches !== 'boolean') {
        return;
      }
      setIsFullscreen(event.matches);
    };

    const handleMediaQueryChange = (event: MediaQueryListEvent | MediaQueryList) => {
      updateFullscreen(event);
    };

    const handleLegacyMediaQueryChange = function (this: MediaQueryList, event: MediaQueryListEvent) {
      updateFullscreen(event);
    };

    const attachListener = () => {
      if (!mediaQuery) return;
      if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', handleMediaQueryChange as EventListener);
      } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(handleLegacyMediaQueryChange);
      }
    };

    const removeListener = () => {
      if (!mediaQuery) return;
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleMediaQueryChange as EventListener);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleLegacyMediaQueryChange);
      }
    };

    if (mediaQuery) {
      updateFullscreen(mediaQuery);
      attachListener();
    }

    return removeListener;
  }, []);

  return (
    <PositionedModal
      open={open}
      onClose={onClose}
      size={isFullscreen ? 'fullscreen' : 'large'}
      closeIcon
      style={isFullscreen ? undefined : { width: '90vw', maxWidth: '1100px', height: '85vh', maxHeight: '90vh' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 360,
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #021241 0%, #03472d 100%)',
          margin: '1px',
        }}
      >

        <div style={{ position: 'relative', marginTop: '1rem', flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {leadershipTeams && leadershipTeams.length > 1 && !tourLoading && !isFirstStep && !isLastStep && (
            <div style={{ position: 'absolute', top: 0, right: 42, zIndex: 10 }}>
              <Select
                value={selectedTeamId != null ? String(selectedTeamId) : undefined}
                onValueChange={(value) =>
                  dispatch(
                    userContextSlice.actions.setSelectedTeamId(value ? Number(value) : null)
                  )
                }
              >
                <SelectTrigger className="pill-dropdown-trigger w-[140px]">
                  <SelectValue>
                    <span>
                      {leadershipTeams.find((team) => String(team.id) === String(selectedTeamId))?.name ??
                        'Select a team'}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {leadershipTeams.map((team) => (
                    <SelectItem key={String(team.id)} value={String(team.id)}>
                      <span>{team.name}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <StepComponent />
        </div>

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            paddingTop: '1rem',
            paddingBottom: '1rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            background: 'white',
            marginTop: '1rem',
            gap: '0.75rem',
          }}
        >
          <Button basic disabled={isFirstStep} onClick={onPrevious}>
            Back
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, justifyContent: 'center' }}>
            <div style={{ display: 'flex', gap: '0.35rem' }}>
              {experience.steps.map((_, index) => (
                <div
                  key={index}
                  data-testid="progress-dot"
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: index === stepIndex ? '#0055ff' : '#d3d3d3',
                  }}
                  aria-hidden="true"
                />
              ))}
            </div>
          </div>
          <Button primary onClick={isLastStep ? onComplete : onNext} loading={isSaving} disabled={isSaving}>
            {isLastStep ? "Let's get started!" : 'Next'}
          </Button>
        </div>
      </div>
    </PositionedModal>
  );
};

export default GuidedExperienceModal;
